import React, { Component } from 'react';
import { toast } from 'react-toastify';
import api from '../../ApiService';

// PKCE utilities - must match Details.js exactly
function base64URLEncode(buffer) {
    return btoa(String.fromCharCode.apply(null, new Uint8Array(buffer)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
}

async function sha256(plain) {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    return await crypto.subtle.digest('SHA-256', data);
}

function generateCodeVerifier() {
    return [...Array(128)].map(() => Math.random().toString(36)[2]).join('');
}

function generateState() {
    return [...Array(32)].map(() => Math.random().toString(36)[2]).join('');
}

const ViewState = Object.freeze({
    INITIAL: 'initial',
    PROCESSING: 'processing',
    SUCCESS: 'success',
    ERROR: 'error'
});

export default class DigitailSetup extends Component {
    displayName = DigitailSetup.name;

    constructor(props) {
        super(props);
        this.state = {
            viewState: ViewState.INITIAL,
            isLoading: false,
            name: '',
            email: '',
            hospitalName: '',
            errors: {}
        };
    }

    componentDidMount() {
        this.handleOAuthCallback();
    }

    handleOAuthCallback = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');

        if (!code || !state) {
            return;
        }

        const storedState = sessionStorage.getItem('digitail_public_oauth_state');
        const codeVerifier = sessionStorage.getItem('digitail_public_code_verifier');
        const signupId = sessionStorage.getItem('digitail_public_signup_id');

        if (state !== storedState) {
            console.error('OAuth state mismatch');
            this.setState({ viewState: ViewState.ERROR });
            this.clearOAuthParams();
            return;
        }

        if (!codeVerifier) {
            console.error('Missing code verifier');
            this.setState({ viewState: ViewState.ERROR });
            this.clearOAuthParams();
            return;
        }

        if (!signupId) {
            console.error('Missing signup ID');
            this.setState({ viewState: ViewState.ERROR });
            this.clearOAuthParams();
            return;
        }

        this.setState({ viewState: ViewState.PROCESSING });

        try {
            const redirectUri = window.location.origin;
            const response = await api.exchangeDigitailPublicToken(
                parseInt(signupId, 10),
                code,
                codeVerifier,
                redirectUri
            );

            if (response && response.success) {
                this.setState({ viewState: ViewState.SUCCESS });
            } else {
                this.setState({ viewState: ViewState.ERROR });
            }
        } catch (error) {
            console.error('Error exchanging token:', error);
            this.setState({ viewState: ViewState.ERROR });
        }

        this.clearOAuthParams();
    };

    clearOAuthParams = () => {
        sessionStorage.removeItem('digitail_public_oauth_state');
        sessionStorage.removeItem('digitail_public_code_verifier');
        sessionStorage.removeItem('digitail_public_signup_id');

        const url = new URL(window.location);
        url.searchParams.delete('code');
        url.searchParams.delete('state');
        window.history.replaceState({}, document.title, url.pathname);
    };

    validateForm = () => {
        const { name, email, hospitalName } = this.state;
        const errors = {};

        if (!name.trim()) {
            errors.name = 'Name is required';
        }

        if (!email.trim()) {
            errors.email = 'Email is required';
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                errors.email = 'Please enter a valid email address';
            }
        }

        if (!hospitalName.trim()) {
            errors.hospitalName = 'Hospital name is required';
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    };

    handleInputChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value });
    };

    onClickConnect = async () => {
        if (!this.validateForm()) {
            return;
        }

        this.setState({ isLoading: true });

        try {
            // Check if email already exists
            const emailExists = await api.checkDigitailEmailExists(this.state.email);
            if (emailExists) {
                this.setState({
                    isLoading: false,
                    errors: { email: 'This email is already registered' }
                });
                return;
            }

            const config = await api.getDigitailPublicConfig();

            if (!config || !config.clientId || !config.authorizationUrl) {
                toast.error('Failed to load Digitail configuration');
                this.setState({ isLoading: false });
                return;
            }

            // Build the authorization URL before saving so we can capture it in the record
            const codeVerifier = generateCodeVerifier();
            const codeChallenge = base64URLEncode(await sha256(codeVerifier));
            const state = generateState();
            const redirectUri = window.location.origin;

            const params = new URLSearchParams({
                response_type: 'code',
                client_id: config.clientId,
                redirect_uri: redirectUri,
                state: state,
                code_challenge: codeChallenge,
                code_challenge_method: 'S256'
            });

            const authUrl = `${config.authorizationUrl}?${params.toString()}`;

            // Create signup record in database before redirecting to OAuth
            const signupResult = await api.createDigitailPublicSignup(
                this.state.name,
                this.state.email,
                this.state.hospitalName,
                authUrl
            );

            if (signupResult.error) {
                toast.error('Failed to create signup');
                this.setState({ isLoading: false });
                return;
            }

            // Store signup ID and OAuth params for callback
            sessionStorage.setItem('digitail_public_code_verifier', codeVerifier);
            sessionStorage.setItem('digitail_public_oauth_state', state);
            sessionStorage.setItem('digitail_public_signup_id', signupResult.id);

            console.log('Redirect URI:', redirectUri);
            window.location.href = authUrl;
        } catch (error) {
            console.error('Error initiating OAuth:', error);
            toast.error('Failed to connect to Digitail');
            this.setState({ isLoading: false });
        }
    };

    renderInitialView() {
        const { name, email, hospitalName, errors, isLoading } = this.state;

        return (
            <div style={{ maxWidth: '30em', margin: 'auto', marginTop: '3em' }}>
                <h2 className="text-center">Digitail Setup</h2>
                <p className="text-center" style={{ marginTop: '2em' }}>
                    Please fill in your details below to connect your Digitail account.
                </p>
                <form style={{ marginTop: '2em' }}>
                    <div className="form-group" style={{ marginBottom: '1em' }}>
                        <label htmlFor="name">Name <span style={{ color: 'red' }}>*</span></label>
                        <input
                            type="text"
                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                            id="name"
                            name="name"
                            value={name}
                            onChange={this.handleInputChange}
                            placeholder="Enter your name"
                        />
                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </div>
                    <div className="form-group" style={{ marginBottom: '1em' }}>
                        <label htmlFor="email">Email <span style={{ color: 'red' }}>*</span></label>
                        <input
                            type="email"
                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                            id="email"
                            name="email"
                            value={email}
                            onChange={this.handleInputChange}
                            placeholder="Enter your email"
                        />
                        {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    </div>
                    <div className="form-group" style={{ marginBottom: '1em' }}>
                        <label htmlFor="hospitalName">Hospital Name <span style={{ color: 'red' }}>*</span></label>
                        <input
                            type="text"
                            className={`form-control ${errors.hospitalName ? 'is-invalid' : ''}`}
                            id="hospitalName"
                            name="hospitalName"
                            value={hospitalName}
                            onChange={this.handleInputChange}
                            placeholder="Enter your hospital name"
                        />
                        {errors.hospitalName && <div className="invalid-feedback">{errors.hospitalName}</div>}
                    </div>
                    <div className="text-center">
                        <button
                            type="button"
                            className="btn btn-primary btn-lg"
                            onClick={this.onClickConnect}
                            disabled={isLoading}
                            style={{ marginTop: '1em' }}
                        >
                            {isLoading ? 'Connecting...' : 'Connect to Digitail'}
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    renderProcessingView() {
        return (
            <div className="text-center" style={{ maxWidth: '30em', margin: 'auto', marginTop: '3em' }}>
                <h2>Processing...</h2>
                <p style={{ marginTop: '2em' }}>
                    Please wait while we complete your registration.
                </p>
            </div>
        );
    }

    renderSuccessView() {
        return (
            <div className="text-center" style={{ maxWidth: '30em', margin: 'auto', marginTop: '3em' }}>
                <h2>Thank you for signing up</h2>
                <p style={{ marginTop: '2em' }}>
                    Your Digitail account has been successfully connected.
                </p>
            </div>
        );
    }

    renderErrorView() {
        return (
            <div className="text-center" style={{ maxWidth: '30em', margin: 'auto', marginTop: '3em' }}>
                <h2>Something went wrong</h2>
                <p style={{ marginTop: '2em' }}>
                    Please contact the administrator.
                </p>
                <button
                    className="btn btn-primary"
                    onClick={() => this.setState({ viewState: ViewState.INITIAL })}
                    style={{ marginTop: '2em' }}
                >
                    Try Again
                </button>
            </div>
        );
    }

    render() {
        const { viewState } = this.state;

        return (
            <div style={{ paddingBottom: '25px' }}>
                {viewState === ViewState.INITIAL && this.renderInitialView()}
                {viewState === ViewState.PROCESSING && this.renderProcessingView()}
                {viewState === ViewState.SUCCESS && this.renderSuccessView()}
                {viewState === ViewState.ERROR && this.renderErrorView()}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/DigitailSetup/DigitailSetup.js